import {
  addMonths,
  differenceInCalendarMonths,
  startOfMonth,
  isBefore,
} from "date-fns";

/**
 * Returns "MM/YYYY" for a given Date
 */
function toMmYyyy(date) {
  const m = date.getMonth() + 1;
  const y = date.getFullYear();
  return String(m).padStart(2, "0") + "/" + y;
}

/**
 * Core rule (period-based):
 * - Contract starts at startOfMonth(contractDate) = S
 * - Initial period: [S, S + D)
 * - Extensions repeat with length E months from I0 = S + D:
 *     [I0, I0 + E), [I0 + E, I0 + 2E), ...
 * - To avoid renewal of the *current* period with end = P_end:
 *     terminate STRICTLY BEFORE cutoff = P_end - noticePeriod months.
 *   (If termination is on or after cutoff, extension periods continue until the notice rule is met.)
 *
 * Result returned as "MM/YYYY" = the *last charged month* (P_end - 1 month),
 * where P_end is the first period end that satisfies the strict notice rule.
 *
 * @param {Object} data
 * @param {Date}   data.contractDate
 * @param {number} data.contractDuration   // months (D)
 * @param {number} data.contractExtension  // months (E), >=1
 * @param {number} data.noticePeriod       // months (N), >=0
 * @param {Date}   data.terminationDate    // when the member gave notice
 * @returns {string} "MM/YYYY" (last charged month)
 */
export function calculateTermination({
  contractDate,
  contractDuration,
  contractExtension,
  noticePeriod,
  terminationDate,
}) {
  if (
    !contractDate ||
    !Number.isFinite(contractDuration) ||
    !Number.isFinite(contractExtension) ||
    !Number.isFinite(noticePeriod) ||
    !terminationDate
  ) {
    throw new Error("calculateTermination: invalid input");
  }

  const D = Math.max(0, Math.trunc(contractDuration));
  const E = Math.max(1, Math.trunc(contractExtension)); // at least 1 month
  const N = Math.max(0, Math.trunc(noticePeriod));

  const S = startOfMonth(contractDate);
  const T = startOfMonth(terminationDate); // treat notice month-based

  const initialEnd = startOfMonth(addMonths(S, D)); // exclusive end of initial term

  // Helper: given a period end date (exclusive), compute lastMonth string
  const lastMonthAtPeriodEnd = (periodEnd) => {
    const lm = startOfMonth(addMonths(periodEnd, -1));
    return toMmYyyy(lm);
  };

  // Start with the period that contains T (or the initial period if still inside it).
  let periodEnd;
  if (isBefore(T, initialEnd)) {
    periodEnd = initialEnd;
  } else {
    const monthsFromInitialEnd = Math.max(
      0,
      differenceInCalendarMonths(T, initialEnd)
    );
    const k = Math.floor(monthsFromInitialEnd / E); // 0-based index for current extension period
    const periodStart = startOfMonth(addMonths(initialEnd, k * E));
    periodEnd = startOfMonth(addMonths(periodStart, E)); // exclusive
  }

  // If notice is too late for the current period, continue extending until it becomes timely.
  let safetyCounter = 0;
  while (safetyCounter < 1200) {
    const cutoff = startOfMonth(addMonths(periodEnd, -N));
    if (isBefore(T, cutoff)) {
      return lastMonthAtPeriodEnd(periodEnd);
    }
    periodEnd = startOfMonth(addMonths(periodEnd, E));
    safetyCounter += 1;
  }

  // Hard fallback for extreme edge cases.
  return lastMonthAtPeriodEnd(periodEnd);
}
